import React, { useState } from 'react';
import styled from 'styled-components';
import {
  finnishWithType,
  prepareMarkdown,
  trimmed,
} from '../utils/helpers';
import translate from '../utils/translate';
import {
  Info,
  ShowMoreButton,
  FloatRight,
  PrintButton,
  Icon,
} from './basic';
import CustomTooltip from './ToolTip';
import ExtraFilterIcon from './ExtraFilterIcon';

import { faPrint } from '@fortawesome/free-solid-svg-icons';

const PrintServiceChannelButton = styled(PrintButton).attrs(
  {
    'aria-label': 'tulosta palvelun tiedot',
  },
)``;

const Details = styled.div``;

const TitleName = styled.h3`
  margin-top: 0;
`;

const InlineName = styled.span`
  font-weight: bold;
`;

const ExtraFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ExtraFiltersListItem = styled.div`
  margin: 4px 16px 4px 0;
`;

const Description = styled.div`
  margin: 32px 0;
`;

const ServiceHeading = styled.div`
  display: flex;
  justify-content: space-between;
`;

/*
  USAGE:
  <ServiceDetails
    data={data}
    view={{
      name: 'header' / 'inline',
      contact: 'full',
      summary: 'full' / 'trimmed',
      description: 'full' / 'trimmed',
      showChargeType: boolean,
    }}
  />
  all view options default to falsy/nullish
*/

function ServiceDetails({ data, view }) {
  const [
    showFullDescription,
    setShowFullDescription,
  ] = useState(false);

  function renderName() {
    const name = data.name;

    switch (view.name) {
      case 'header':
        return (
          <ServiceHeading>
            <TitleName>{name}</TitleName>
            <FloatRight>
              <PrintServiceChannelButton
                onClick={printSelections}
                value={data.id}
              >
                <Icon icon={faPrint} />
                Tulosta
              </PrintServiceChannelButton>
            </FloatRight>
          </ServiceHeading>
        );

      case 'inline':
        return <InlineName>{name}: </InlineName>;

      default:
        return null;
    }
  }

  function renderSummary() {
    const summary = data.description.find(
      finnishWithType('Summary'),
    )?.value;

    switch (view.summary) {
      case 'full':
        return (
          <Info>
            <strong>{summary}</strong>
          </Info>
        );

      case 'trimmed':
        return trimmed(summary);

      default:
        return null;
    }
  }

  function renderDescription() {
    const description = data.description.find(
      finnishWithType('Description'),
    )?.value;

    switch (view.description) {
      case 'full':
        if (
          description.length > 200 &&
          !showFullDescription
        ) {
          return (
            <Description>
              <p>
                {trimmed(description)}
                <ShowMoreButton
                  className="show-more-button dont-print"
                  onClick={() =>
                    setShowFullDescription(true)
                  }
                >
                  Näytä kokonaan
                </ShowMoreButton>
              </p>
            </Description>
          );
        }
        return (
          <Description
            dangerouslySetInnerHTML={{
              __html: prepareMarkdown(description),
            }}
          />
        );

      case 'trimmed':
        return trimmed(description);

      default:
        return null;
    }
  }

  const renderExtraFilters = () => (
    <ExtraFilters>
      {translate(data.serviceChargeType) === 'maksuton' && (
        <ExtraFiltersListItem>
          <CustomTooltip toolTipText="Maksuton palvelu">
            <ExtraFilterIcon type="maksuton" />
          </CustomTooltip>
        </ExtraFiltersListItem>
      )}
      {data.serviceVoucherInUse === true && (
        <ExtraFiltersListItem>
          <CustomTooltip toolTipText="Palveluseteli käytössä">
            <ExtraFilterIcon type="palveluseteli" />
          </CustomTooltip>
        </ExtraFiltersListItem>
      )}

      {data.languages.includes('sv') && (
        <ExtraFiltersListItem>
          <CustomTooltip toolTipText="Palvelu saatavilla myös ruotsiksi">
            <ExtraFilterIcon type="ruotsi" />
          </CustomTooltip>
        </ExtraFiltersListItem>
      )}

      {data.languages.includes('en') && (
        <ExtraFiltersListItem>
          <CustomTooltip toolTipText="Palvelu saatavilla myös englanniksi">
            <ExtraFilterIcon type="englanti" />
          </CustomTooltip>
        </ExtraFiltersListItem>
      )}
    </ExtraFilters>
  );

  function printSelections(evt) {
    const currentTarget = evt.currentTarget.value;
    const element = document.getElementById(
      'print-item-' + currentTarget,
    );

    // open downsized description texts before printing
    const servicePrintButton = element.querySelector(
      'div p button',
    );

    if (servicePrintButton) {
      servicePrintButton.click();
    }

    element.parentNode
      .querySelectorAll('.service-channel-box')
      .forEach((button) => {
        let serviceDetailButton = button.querySelector(
          'div p button',
        );
        if (serviceDetailButton) {
          serviceDetailButton.click();
        }
      });

    element.classList.remove('dont-print');
    element.classList.add('service-margin');
    window.print();
    element.classList.add('dont-print');
    element.classList.remove('service-margin');
  }

  return !data ? null : (
    <Details
      id={'print-item-' + data.id}
      className="dont-print"
    >
      {!!view.name && renderName()}

      {!!view.summary && renderSummary()}

      {renderExtraFilters()}

      {!!view.description && renderDescription()}
    </Details>
  );
}

export default ServiceDetails;
